import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { io, Socket } from 'socket.io-client';
import { WebAddresses } from '../app.config';
import { globalUser } from '../helpers/globalFields';
import { Observable } from 'rxjs';

/**
 * Service that manages the Socket.IO connection for the current user session.
 *
 * On connect it joins the org room for live ticket count updates.
 * It also listens for 'permissionsChanged' events emitted by the server when
 * an admin modifies the current user's permission assignments. On receipt,
 * it automatically calls the refresh endpoint to obtain a new token so the
 * updated permission set takes effect immediately across all open tabs/windows
 * without requiring a re-login.
 *
 * @author julian.f.verhey
 */
@Injectable({
  providedIn: 'root',
})
export class SocketService implements OnDestroy {
  private socket: Socket | null = null;
  private readonly base = WebAddresses.tCCTicketServer;
  constructor(
    private http: HttpClient,
    private router: Router,
    private snackBar: MatSnackBar,
  ) {}

  /**
   * Initialize the socket connection using the current access token as auth.
   * Should be called once after a successful login.
   */
  connect(): void {
    if (this.socket?.connected) return;
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    this.socket = io(this.base, {
      auth: { token },
    });

    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket?.id);
    });

    this.socket.on('disconnect', () => {
      console.log('Socket disconnected');
    });

  }



  /**
   * Disconnect and reconnect the socket using the latest access token.
   * Called after a token refresh so the new JWT is used for auth.
   */
  reconnect(): void {
    this.disconnect();
    this.connect();
  }

  /**
   * Emits when a new ticket is opened in the org room.
   * @returns Observable emitting the new open ticket count
   */
  onTicketOpened(): Observable<number> {
    return new Observable((observer) => {
      this.socket?.on('ticketOpened', (count: number) => {
        observer.next(count);
      });
    });
  }

  /**
   * Emits when the open pCC ticket count changes for the org.
   * @returns Observable emitting the updated count
   */
  onValueChanged(): Observable<number> {
    return new Observable((observer) => {
      this.socket?.on('valueChanged', (count: number) => {
        observer.next(count);
      });
    });
  }

  /**
   * Gracefully disconnect the socket.
   * Should be called on logout or app teardown.
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  private logout(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    this.disconnect();
    this.router.navigate(['/login']);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
